import { useEffect, useRef, useState } from 'react'
import { useStore } from '@tanstack/react-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ClienteCombobox } from '@/components/ui/cliente-combobox'
import { useClientes, useAgregarClienteResponsable } from '@/hooks/useClientes'
import { useVencimientosCliente } from '@/hooks/useCalendario'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import { usePermissions } from '@/hooks/usePermissions'
import { useSupabase } from '@/lib/supabase-context'
import { clientesService } from '@/services/clientesService'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { requiereChequeoAccesoCliente } from './delegacionCrossCliente'
import { RecurrenciaSection } from './RecurrenciaSection'
import {
  autocompletarDiaDelMes,
  diaDelMesDeFecha,
} from '@/components/tareas/forms/recurrenciaSchema'
import { GrupoClientesSection } from './GrupoClientesSection'
import { VencimientoField } from './VencimientoField'
import type { TareaCompleta } from '@/types/vencimientos'
import type { TareaRecurrente } from '@/core/services/tareasRecurrentesService'
import type { TipoGrupoTarea } from '@/core/services/tareasGrupoService'
import { Loader2 } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import {
  useTareaForm,
  getFieldError,
  DEFAULT_TAREA_VALUES,
  type TareaFormValues,
} from '@/components/tareas/forms'

// Re-export para compatibilidad
export type { TareaFormValues }

interface TareaFormProps {
  tarea?: TareaCompleta | null
  onSubmit: (data: TareaFormValues) => void
  onCancel: () => void
  isLoading?: boolean
  /** ORR-327: valores iniciales al crear (ej. el cliente desde cuya ficha se abrió). */
  defaultValues?: Partial<TareaFormValues>
  /** Ofrecer "Repetir esta tarea". Solo el caller que sepa procesar `repetir` y los
      `rec_*` del submit debe activarlo: `tareasService.crear` los ignora, así que
      mostrarlo en un alta rápida promete una repetición que nunca se crea. */
  canRepeat?: boolean
  /** Repetición guardada a editar. El formulario muestra su regla y su destino, sin vencimiento
      ni fecha límite. El submit trae `rec_*` y `grupo_*` con los valores editados. */
  recurrente?: TareaRecurrente | null
  /** Receives whether the form has unsaved changes. Reports false on unmount. */
  onDirtyChange?: (dirty: boolean) => void
}

/** Mapea una repetición guardada a valores del formulario. */
function mapRecurrenteToFormValues(r: TareaRecurrente): Partial<TareaFormValues> {
  const grupo_tipo: TipoGrupoTarea | null = r.etiqueta_id
    ? 'etiqueta'
    : r.condicion_iva
      ? 'condicion_iva'
      : r.impuesto_id
        ? 'impuesto'
        : r.tipo_persona
          ? 'tipo_persona'
          : null
  return {
    titulo: r.titulo,
    descripcion: r.descripcion,
    cliente_id: r.cliente_id,
    asignado_a: r.asignado_a,
    prioridad: r.prioridad as TareaFormValues['prioridad'],
    repetir: true,
    rec_frecuencia: r.frecuencia,
    rec_dia_modo: r.dia_modo,
    rec_dia_del_mes: r.dia_del_mes ?? DEFAULT_TAREA_VALUES.rec_dia_del_mes,
    rec_dia_semana: r.dia_semana,
    rec_correr_a_habil: r.correr_a_habil,
    grupo_tipo,
    grupo_valor: r.etiqueta_id ?? r.condicion_iva ?? r.impuesto_id ?? r.tipo_persona,
  }
}

/**
 * Mapea una tarea existente a valores del formulario
 */
function mapTareaToFormValues(tarea: TareaCompleta): Partial<TareaFormValues> {
  return {
    titulo: tarea.titulo ?? '',
    descripcion: tarea.descripcion ?? null,
    cliente_id: tarea.cliente_id ?? null,
    vencimiento_id: tarea.vencimiento_id ?? null,
    asignado_a: tarea.asignado_a ?? null,
    prioridad: (tarea.prioridad as TareaFormValues['prioridad']) ?? 'media',
    fecha_limite: tarea.fecha_limite ?? null,
  }
}

// Pregunta del diálogo de acceso, según si el usuario puede agregar responsables al cliente.
const PREGUNTA_ACCESO = {
  conPermiso: '¿Querés darle acceso al cliente y asignarle la tarea?',
  sinPermiso:
    'Darle acceso requiere el permiso «Asignar responsables». Podés asignarle la tarea igual: la va a ver en Tareas.',
}

export function TareaForm({
  tarea,
  onSubmit,
  onCancel,
  isLoading,
  defaultValues,
  canRepeat = false,
  recurrente,
  onDirtyChange,
}: TareaFormProps) {
  // Obtener clientes activos
  const { data: clientes = [], isLoading: loadingClientes } = useClientes({
    estado: 'Activo',
  })

  // Obtener usuarios activos
  const { data: usuarios = [] } = useUsuariosOrg({ activo: true })
  const { rolMultiTenant, emailUsuario, puede } = usePermissions()
  const canDelegate = ['owner', 'admin', 'profesional'].includes(rolMultiTenant ?? '')
  // Dar acceso agrega un responsable al cliente y exige ese permiso. Sin él, solo se asigna.
  const puedeDarAcceso = !!puede.asignarResponsablesCliente

  // ORR-202: al delegar una tarea de un cliente que el asignado NO tiene, se le avisa al
  // admin y se le ofrece darle acceso (opción a). El chequeo corre en el submit.
  const { supabase } = useSupabase()
  const agregarResponsable = useAgregarClienteResponsable()
  const [pendingAccess, setPendingAccess] = useState<{
    values: TareaFormValues
    usuarioId: string
    usuarioNombre: string
    clienteId: string
  } | null>(null)

  const handleFormSubmit = async (values: TareaFormValues) => {
    const asignado = usuarios.find((u) => u.email === values.asignado_a)
    if (!requiereChequeoAccesoCliente(values, asignado, emailUsuario)) {
      onSubmit(values)
      return
    }
    // El predicado garantiza cliente_id y asignado definidos y no-admin.
    const responsables = await clientesService.obtenerResponsables(supabase!, values.cliente_id!)
    if (responsables.includes(asignado!.id)) {
      onSubmit(values)
      return
    }
    setPendingAccess({
      values,
      usuarioId: asignado!.id,
      usuarioNombre: asignado!.nombre ?? asignado!.email,
      clienteId: values.cliente_id!,
    })
  }

  const handleConfirmarAcceso = async () => {
    if (!pendingAccess) return
    if (puedeDarAcceso) {
      await agregarResponsable.mutateAsync({
        clienteId: pendingAccess.clienteId,
        usuarioId: pendingAccess.usuarioId,
      })
    }
    onSubmit(pendingAccess.values)
    setPendingAccess(null)
  }

  // Auto-assign: set in defaultValues to avoid timing issues
  const autoAssignDefaults = !canDelegate && emailUsuario && !tarea && !recurrente
    ? { asignado_a: emailUsuario }
    : undefined

  // Un alta que llega con fecha límite toma su día para la repetición. Un `rec_dia_del_mes` de
  // `defaultValues` gana.
  const diaInicial = canRepeat ? diaDelMesDeFecha(defaultValues?.fecha_limite) : null

  const { form } = useTareaForm({
    defaultValues: tarea
      ? mapTareaToFormValues(tarea)
      : recurrente
        ? mapRecurrenteToFormValues(recurrente)
        : {
            ...autoAssignDefaults,
            ...(diaInicial != null && { rec_dia_del_mes: diaInicial }),
            ...defaultValues,
          },
    onSubmit: handleFormSubmit,
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)
  useEffect(() => {
    onDirtyChange?.(isDirty)
    return () => onDirtyChange?.(false)
  }, [isDirty, onDirtyChange])

  // Leer cliente_id reactivamente del form store
  const clienteSeleccionado = useStore(form.store, (s) => s.values.cliente_id) ?? null
  const prevClienteRef = useRef(clienteSeleccionado)

  // Obtener vencimientos del cliente seleccionado
  const { data: vencimientos = [], isLoading: loadingVencimientos } =
    useVencimientosCliente(clienteSeleccionado ?? '')

  // Limpiar vencimiento cuando cambia el cliente
  useEffect(() => {
    if (clienteSeleccionado !== prevClienteRef.current) {
      prevClienteRef.current = clienteSeleccionado
      form.setFieldValue('vencimiento_id', null)
    }
  }, [clienteSeleccionado]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {/* Titulo */}
      <form.Field name="titulo">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Titulo *</Label>
            <Input
              id={field.name}
              placeholder="Descripcion breve de la tarea"
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive">
                {getFieldError(field.state.meta.errors)}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {/* Descripcion */}
      <form.Field name="descripcion">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Descripcion</Label>
            <Textarea
              id={field.name}
              placeholder="Detalles adicionales de la tarea..."
              rows={3}
              value={field.state.value ?? ''}
              onChange={(e) => field.handleChange(e.target.value || null)}
              onBlur={field.handleBlur}
            />
          </div>
        )}
      </form.Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cliente */}
        <form.Field name="cliente_id">
          {(field) => (
            <div className="space-y-2">
              <Label>Cliente</Label>
              <ClienteCombobox
                clientes={clientes}
                value={field.state.value ?? ''}
                onValueChange={(value) => field.handleChange(value || null)}
                placeholder="Buscar cliente..."
                disabled={loadingClientes}
                showAllOption={{ value: '', label: 'Sin cliente' }}
              />
            </div>
          )}
        </form.Field>

        {/* Vencimiento (solo si hay cliente seleccionado). Una repetición no lo guarda. */}
        {!recurrente && (
          <VencimientoField
            form={form}
            clienteSeleccionado={clienteSeleccionado}
            vencimientos={vencimientos}
            loading={loadingVencimientos}
          />
        )}
      </div>

      {/* Urgente checkbox */}
      <form.Field name="prioridad">
        {(field) => (
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={field.state.value === 'urgente'}
              onCheckedChange={(checked) => field.handleChange(checked ? 'urgente' : 'media')}
            />
            <span className="text-sm font-medium text-destructive">Marcar como urgente</span>
          </label>
        )}
      </form.Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Asignado a — RBAC: solo owner/admin/profesional delegan */}
        <form.Field name="asignado_a">
          {(field) => (
            <div className="space-y-2">
              <Label>Asignado a</Label>
              {canDelegate ? (
                <Select
                  onValueChange={(value) => field.handleChange(value === '_none' ? null : value)}
                  value={field.state.value ?? '_none'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sin asignar</SelectItem>
                    {usuarios.map((usuario) => (
                      <SelectItem key={usuario.id} value={usuario.email}>
                        {usuario.nombre || usuario.email.split('@')[0]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={emailUsuario ?? ''} disabled className="text-muted-foreground" />
              )}
            </div>
          )}
        </form.Field>

        {/* Fecha limite. En una repetición la fecha de cada tarea sale de la regla. */}
        {!recurrente && (
        <form.Field
          name="fecha_limite"
          listeners={{
            // En el alta, el día de la fecha límite completa el "Día del mes" de la repetición.
            onChange: ({ value }) => {
              if (!tarea && canRepeat) autocompletarDiaDelMes(form, value)
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>Fecha limite</Label>
              <Input
                id={field.name}
                type="date"
                value={field.state.value ?? ''}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
              />
            </div>
          )}
        </form.Field>
        )}
      </div>

      {/* Repetición (ORR-304). Se muestra al crear, y al editar una repetición guardada. Una
          tarea ya generada no la muestra: su regla se edita desde el panel de repeticiones. */}
      {((!tarea && canRepeat) || recurrente) && (
        <RecurrenciaSection form={form} edicion={!!recurrente} />
      )}

      {/* Grupo de clientes, para una tarea suelta o para una repetición. Mismo gate que
          arriba: solo el caller que sepa procesar `grupo_tipo`/`grupo_valor` en el submit
          lo ofrece. */}
      {((!tarea && canRepeat) || recurrente) && <GrupoClientesSection form={form} />}

      {/* Botones */}
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading || pendingAccess !== null}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {recurrente ? 'Guardar cambios' : `${tarea ? 'Actualizar' : 'Crear'} Tarea`}
        </Button>
      </div>
    </form>

    <AlertDialog open={pendingAccess !== null} onOpenChange={(open) => { if (!open) setPendingAccess(null) }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>El usuario no tiene acceso a este cliente</AlertDialogTitle>
          <AlertDialogDescription>
            {pendingAccess?.usuarioNombre} no es responsable del cliente seleccionado, así que no lo
            verá en su lista. {puedeDarAcceso ? PREGUNTA_ACCESO.conPermiso : PREGUNTA_ACCESO.sinPermiso}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setPendingAccess(null)}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); void handleConfirmarAcceso() }}
            disabled={agregarResponsable.isPending}
          >
            {agregarResponsable.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {puedeDarAcceso ? 'Darle acceso y asignar' : 'Asignar igual'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  )
}
